import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  CircularProgress,
  IconButton,
  Link as MuiLink,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  ArrowBackIos as ArrowBackIosIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { UserContext } from '../auth/auth-context';
import { useSelectedBusiness } from '../../redux/store';
import { GET_STAFF_BY_URL } from '../../graphql/queries';
import { GET_COIN_BALANCE, GET_TIP_ITEMS } from '../../graphql/queries/tip';
import { TIP_WITH_PLANY_COINS } from '../../graphql/mutations/tip';

export default function TipPage() {
  const { businessUrl, staffUrl } = useParams();
  const navigate = useNavigate();
  const { providerUser } = useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const userId = providerUser?.userId;

  // Wallet link
  const walletLink = `/${selectedBusiness?.businessBasic?.url}/${providerUser?.url}/wallet`;

  // Queries
  const { data: staffData, loading: staffLoading } = useQuery(
    GET_STAFF_BY_URL,
    { variables: { businessUrl, staffUrl } }
  );
  const { data: itemsData, loading: itemsLoading } = useQuery(GET_TIP_ITEMS);
  const { data: walletData, loading: walletLoading } = useQuery(
    GET_COIN_BALANCE,
    { variables: { userId }, skip: !userId }
  );

  // Mutation
  const [tipWithCoins, { loading: tipping }] = useMutation(
    TIP_WITH_PLANY_COINS,
    { refetchQueries: [{ query: GET_COIN_BALANCE, variables: { userId } }] }
  );

  // State
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [purpose, setPurpose] = useState('support');
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!providerUser?.defaultBusinessId) {
      // ✅ Store current page as the redirect target
      localStorage.setItem(
        "redirectAfterBusiness",
        window.location.pathname + window.location.search
      );
    }
  }, [providerUser?.defaultBusinessId]);

  // Loading/fallback
  if (staffLoading || itemsLoading || walletLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // 2) Only after loading do we check for missing staff
  if (!staffData?.staffByUrl) {
    return (
      <Box p={3}>
        <Typography color="error">Staff not found.</Typography>
      </Box>
    );
  }
  // Data
  const staff = staffData.staffByUrl;
  const userName = staff.user.username;
  const coinBalance = walletData?.getPlanyCoinWallet?.balance ?? 0;
  const isCreator = Number(providerUser?.userId) === Number(staff?.user?.id);

  // URL share
  const tipPageUrl = `/${businessUrl}/${staffUrl}/tip`;
  const fullTipUrl = `${window.location.origin}${tipPageUrl}`;
  const copyUrl = () => {
    navigator.clipboard.writeText(fullTipUrl)
      .then(() => setCopied(true))
      .finally(() => setTimeout(() => setCopied(false), 2000));
  };

  // Handlers
  const handleTipClick = (item) => { setSelectedItem(item); setPurpose('support'); setNote(''); setOpen(true); };
  const handleConfirmTip = async () => {
    if (!selectedItem) return;
    await tipWithCoins({ variables: { input: { targetUserId: staff.user.id, item: selectedItem.id, note: ['shoutout','question'].includes(purpose)?note:'' } } });
    alert('🎉 Tip sent!'); setOpen(false);
  };

  // Group tiers
  const grouped = { Micro: [], Standard: [], Premium: [], Elite: [], Mega: [] };
  itemsData.getTipItems.forEach(item => {
    if (item.price <= 3) grouped.Micro.push(item);
    else if (item.price <= 15) grouped.Standard.push(item);
    else if (item.price <= 100) grouped.Premium.push(item);
    else if (item.price <= 500) grouped.Elite.push(item);
    else grouped.Mega.push(item);
  });

  // Render in reverse order
  const tierOrder = ['Mega','Elite','Premium','Standard','Micro'];

  return (
    <Box p={3}>
      <Button startIcon={<ArrowBackIosIcon />} onClick={() => navigate(walletLink)} sx={{ mb: 2 }}>Back to Wallet</Button>
      <Typography variant="h5" gutterBottom>{isCreator? 'Your Tip Page': `💡 Tip ${userName}`}</Typography>

      {isCreator && (
        <Paper sx={{ p:2, mb:3 }} elevation={1}>
          <Box display="flex" alignItems="center">
            <MuiLink component={Link} to={tipPageUrl} sx={{ wordBreak:'break-all',fontWeight:500 }}>{fullTipUrl}</MuiLink>
            <IconButton size="small" onClick={copyUrl} sx={{ ml:1 }}>{copied?<CheckIcon/>:<CopyIcon/>}</IconButton>
          </Box>
 <Typography sx={{ mt: 1, color: '#00695c' }}>
            Copy & share this link on your channel, website, or social media.
          </Typography>        </Paper>
      )}

      {!isCreator && (
        <Box sx={{ mb:4 }}>
          <Typography>🪙 You have <strong>{coinBalance}</strong> PlanyCoins <Button component={Link} to="/coins/buy" sx={{ ml:1 }}>➕ Buy More</Button></Typography>
          
        </Box>
      )}

      {tierOrder.map(tier => (
        <Box key={tier} sx={{ mb:2 }}>
          <Typography variant="h6" sx={{ mb:1 }}>{tier}</Typography>
          <Box sx={{ display:'flex', flexWrap:'wrap', gap:2 }}>
            {grouped[tier].map(item => {
              const disabled = tipping || coinBalance < item.price || isCreator;
              let tooltipTitle = '';
              if (isCreator) tooltipTitle = "You cannot tip yourself";
              else if (coinBalance < item.price) tooltipTitle = "Not enough PlanyCoins";

              const btn = (
                <Button key={item.id} onClick={()=>handleTipClick(item)} disabled={disabled} variant="outlined" sx={{width:90,height:90,borderRadius:2,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',p:1,textAlign:'center'}}>
                  <Typography variant="h4" component="span">{item.emoji}</Typography>
                  <Typography variant="caption">{item.name}</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">{item.price} 🪙</Typography>
                </Button>
              );
              return (
                <Tooltip
                  key={item.id}
                  title={tooltipTitle}
                  arrow
                  disableHoverListener={!tooltipTitle}
                >
                  <span>{btn}</span>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      ))}

      <Dialog open={open} onClose={()=>setOpen(false)}>
        <DialogTitle>Customize Your Tip</DialogTitle>
        <DialogContent>
          <Select fullWidth value={purpose} onChange={e=>setPurpose(e.target.value)} sx={{mb:2}}>
            <MenuItem value="support">General Support</MenuItem>
            <MenuItem value="shoutout">Shoutout</MenuItem>
            <MenuItem value="question">Ask a Question</MenuItem>
          </Select>
          {['shoutout','question'].includes(purpose)&&<TextField multiline rows={3} fullWidth label="Message" value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Say hi!" sx={{mb:2}} />}
          {selectedItem && <Tooltip title={`Deducts ${selectedItem.price} 🪙 (~$${(selectedItem.price*0.1).toFixed(2)} USD)`}><Typography variant="caption" color="textSecondary">Tip Cost: {selectedItem.price} PlanyCoins</Typography></Tooltip>}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmTip} variant="contained" disabled={!selectedItem||tipping||coinBalance<(selectedItem?.price||0)}>
            {tipping?<CircularProgress size={20}/>:'Confirm Tip'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
